import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, Observable, of, tap } from 'rxjs';

import {
  AccountDTO,
  AircraftDTO,
  AircraftModelDTO,
  AttachmentsDTO,
  ContactType,
  CountryDTO,
  FileStatusDTO,
  EspEnrollmentFormDto,
  FormStep,
  MappedFiles,
  OperationType,
  ContractDTO,
  ReviewPdfDTO,
  AccountContactDTO,
} from './../../shared-utils/models/esp-enrollment.model';
import { ConfigurationService } from './configuration.service';
import { SharedDataService } from './shared-data.service';

@Injectable({
  providedIn: 'root',
})
export class EspDataService {
  private _http = inject(HttpClient);
  private _config = inject(ConfigurationService);
  private _sharedDataService = inject(SharedDataService);
  private _bffURL = this._config.configuration().bffURL;
  private _awsURL = this._config.configuration().awsURL;
  private _aircraftsURL = `${this._bffURL}/${this._config.configuration().aircraftsURL}`;
  private _enrollmentURL = `${this._bffURL}/${this._config.configuration().enrollmentURL}`;
  private _regionURL = `${this._bffURL}/${this._config.configuration().regionURL}`;
  private _partyURL = `${this._bffURL}/${this._config.configuration().partyURL}`;
  private _fileURL = `${this._bffURL}/${this._config.configuration().fileURL}`;
  private _fileUploadURL = `${this._awsURL}/${this._config.configuration().fileUploadURL}`;
  private _bffFileURL = `${this._bffURL}/${this._config.configuration().bffFileURL}`;
  private _apiClientId = `${this._config.configuration().apiClientId}`;
  private _apiBffClientId = `${this._config.configuration().apiBffClientId}`;

  private referenceNumber = this._sharedDataService.requestReferenceNumber;
  authToken = this._sharedDataService.token();
  getAircraftModels(): Observable<AircraftModelDTO[]> {
    const headers = new HttpHeaders().set('X-API-ID', this._apiBffClientId);
    return this._http.get<AircraftModelDTO[]>(`${this._aircraftsURL}/models`, {
      headers,
    });
  }

  getAircraftDetails(
    aircraftSerialNo: string,
    aircraftModel: string,
  ): Observable<AircraftDTO> {
    const headers = new HttpHeaders().set('X-API-ID', this._apiBffClientId);
    return this._http
      .post<AircraftDTO>(
        `${this._aircraftsURL}`,
        {
          aircraftSerialNumber: aircraftSerialNo,
          aircraftModel: aircraftModel,
        },
        { headers },
      )
      .pipe(
        catchError((error) => {
          if (error.status === 404) {
            this._sharedDataService.isEngineManualEntry.set(true);
          }
          return of(error);
        }),
      );
  }

  getOperationTypes(): Observable<OperationType[]> {
    const headers = new HttpHeaders().set('X-API-ID', this._apiBffClientId);
    return this._http
      .get<OperationType[]>(`${this._aircraftsURL}/operations`, { headers })
      .pipe(catchError((error) => of(error)));
  }

  createEnrollmentForm(formValues: FormStep) {
    const headers = new HttpHeaders().set('X-API-ID', this._apiBffClientId);
    console.log('submitAircraftInformation ', { ...formValues });
    return this._http
      .post<{
        id: string;
        requestReferenceNumber: string;
      }>(`${this._enrollmentURL}/step1`, formValues, { headers })
      .pipe(
        tap((response) => {
          this.referenceNumber.set(response.id);
          this._sharedDataService.requestInstanceNumber.set(
            response.requestReferenceNumber,
          );
        }),
      );
  }

  updateEnrollmentForm(
    formValues: FormStep,
    step: number,
    contactType?: ContactType,
  ) {
    console.log('update step', step, { ...formValues });
    const headers = new HttpHeaders().set('X-API-ID', this._apiBffClientId);
    return this._http.put<FormStep>(
      `${this._enrollmentURL}/step${step}/${this.referenceNumber()}`,

      formValues,
      contactType ? { headers, params: { contactType } } : { headers },
    );
  }

  getEnrollmentForm(referenceNumber: string) {
    const headers = new HttpHeaders().set('X-API-ID', this._apiBffClientId);
    return this._http.get<EspEnrollmentFormDto>(
      `${this._enrollmentURL}/${referenceNumber}`,
      { headers },
    );
  }

  submitEnrollmentForm(
    referenceNumber: string,
    isInterestedInCarbonOffset: boolean,
    requesterBadgeNumber: string,
    customerCode: string,
    requesterName: string,
    requesterEmail: string,
    requesterCompanyName: string,
  ) {
    console.log('submit form');
    const headers = new HttpHeaders().set('X-API-ID', this._apiBffClientId);
    return this._http.post<void>(
      `${this._enrollmentURL}/${referenceNumber}/submission`,
      {
        isInterestedInCarbonOffset,
        requesterBadgeNumber,
        customerCode,
        requesterName,
        requesterEmail,
        requesterCompanyName,
      },
      { headers },
    );
  }

  getCountries() {
    console.log('get countries');
    const headers = new HttpHeaders().set('X-API-ID', this._apiBffClientId);
    return this._http.get<CountryDTO[]>(`${this._regionURL}/countries`, {
      headers,
    });
  }

  getAccounts(country: string, zipCode: string, companyName: string) {
    console.log('get accounts');
    const headers = new HttpHeaders().set('X-API-ID', this._apiBffClientId);
    return this._http.get<AccountDTO[]>(`${this._partyURL}/accounts`, {
      headers,
      params: { country, zipCode, companyName },
    });
  }

  uploadFilesAndProcess(
    files: MappedFiles,
    onSucess: (filenames: AttachmentsDTO[]) => void,
    onError: (error: any) => void,
  ) {
    this.uploadFiles(files).subscribe({
      next: (res: AttachmentsDTO[]) => {
        onSucess(res);
      },
      error: (err) => {
        onError(err);
      },
    });
  }

  deleteDraftFiles(requestId: string, fileRequestId: number) {
    const headers = new HttpHeaders().set('X-API-ID', this._apiBffClientId);
    return this._http.delete(
      `${this._bffFileURL}/domain/${requestId}/${fileRequestId}`,
      { headers },
    );
  }

  deleteAWSFiles(filename: string) {
    const headers = new HttpHeaders().set('X-API-ID', this._apiBffClientId);
    return this._http.delete(`${this._bffFileURL}/aws/${filename}`, {
      headers,
    });
  }

  getFilesStatus(filenames: string[]) {
    const headers = new HttpHeaders().set('X-API-ID', this._apiBffClientId);
    const body = {
      filenames: filenames,
    };
    return this._http.post<FileStatusDTO>(`${this._fileURL}/status`, body, {
      headers,
    });
  }

  private uploadFiles(payload: {
    domain: string;
    attachments: {
      filename: string;
      content: string;
      metadata: {
        espRequestID: string;
        category: string;
      };
    }[];
  }): Observable<AttachmentsDTO[]> {
    const headers = new HttpHeaders().set('X-API-ID', this._apiClientId);

    const body = {
      domain: payload.domain,
      attachments: payload.attachments,
    };
    return this._http.post<AttachmentsDTO[]>(`${this._fileUploadURL}`, body, {
      headers,
    });
  }

  getContract(contractNumber: string) {
    const headers = new HttpHeaders().set('X-API-ID', this._apiBffClientId);
    return this._http.get<ContractDTO>(
      `${this._enrollmentURL}/contract/${contractNumber}`,
      { headers },
    );
  }

  getAccountContactbyCustomerCode(customerCode: string) {
    const headers = new HttpHeaders().set('X-API-ID', this._apiBffClientId);
    return this._http.get<AccountContactDTO>(
      `${this._enrollmentURL}/accounts/${customerCode}`,
      { headers },
    );
  }

  getAccountsbyCustomerCode(customerCode: string) {
    const headers = new HttpHeaders().set('X-API-ID', this._apiBffClientId);
    return this._http.get<AccountDTO>(
      `${this._enrollmentURL}/accounts/${customerCode}`,
      { headers },
    );
  }

  getReviewPdfByRequestId(requestId: string) {
    const headers = new HttpHeaders().set('X-API-ID', this._apiBffClientId);
    return this._http.get<ReviewPdfDTO>(
      `${this._enrollmentURL}/reviewpdf/${requestId}`,
      { headers },
    );
  }
}
